import { useState, useEffect, useMemo } from 'react';
import { useWindowSize } from '../../../../hooks';
import {
  BreakpointConfig,
  getCurrentBreakpoint,
  isMobileBreakpoint,
  isTabletBreakpoint,
  isDesktopBreakpoint,
  isTvBreakpoint,
} from '../layout/breakpoints';
import { BreakpointKey } from '../types';

// ============================================================================
// useBreakpoint Hook
// ============================================================================

export interface UseBreakpointReturn {
  /** Current breakpoint configuration */
  breakpoint: BreakpointConfig;
  /** Current breakpoint key */
  breakpointKey: BreakpointKey;
  /** Whether current viewport is mobile-sized */
  isMobile: boolean;
  /** Whether current viewport is tablet-sized */
  isTablet: boolean;
  /** Whether current viewport is desktop-sized */
  isDesktop: boolean;
  /** Whether current viewport is TV/large display-sized */
  isTv: boolean;
  /** Current viewport width */
  width: number;
  /** Current viewport height */
  height: number;
}

/**
 * Hook to get current breakpoint information for responsive dashboard layout.
 * Uses window resize events to update breakpoint in real-time.
 */
export function useBreakpoint(): UseBreakpointReturn {
  const { width, height } = useWindowSize();

  const breakpoint = useMemo(() => getCurrentBreakpoint(width), [width]);

  const breakpointKey = breakpoint.name;

  return {
    breakpoint,
    breakpointKey,
    isMobile: isMobileBreakpoint(breakpointKey),
    isTablet: isTabletBreakpoint(breakpointKey),
    isDesktop: isDesktopBreakpoint(breakpointKey),
    isTv: isTvBreakpoint(breakpointKey),
    width,
    height,
  };
}

// ============================================================================
// useBreakpointValue Hook
// ============================================================================

type BreakpointValues<T> = Partial<Record<BreakpointKey, T>> & { base: T };

/**
 * Hook to get a value based on current breakpoint.
 * Similar to Chakra UI's useBreakpointValue.
 *
 * @example
 * const columns = useBreakpointValue({ base: 1, md: 2, lg: 3 });
 */
export function useBreakpointValue<T>(values: BreakpointValues<T>): T {
  const { breakpointKey } = useBreakpoint();

  return useMemo(() => {
    // Priority order for fallback (larger to smaller)
    const fallbackOrder: BreakpointKey[] = ['tv', 'xl', 'lg', 'md', 'sm', 'base'];
    const currentIndex = fallbackOrder.indexOf(breakpointKey);

    // Try current breakpoint first, then fall back to smaller breakpoints
    for (let i = currentIndex; i < fallbackOrder.length; i++) {
      const bp = fallbackOrder[i];
      if (values[bp] !== undefined) {
        return values[bp] as T;
      }
    }

    // Always return base as final fallback
    return values.base;
  }, [breakpointKey, values]);
}

// ============================================================================
// useMediaQuery Hook (for custom queries)
// ============================================================================

/**
 * Hook to check if a media query matches.
 *
 * @example
 * const isLargeScreen = useMediaQuery('(min-width: 1200px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Add listener
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export default useBreakpoint;
